import { useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { hasPermission, useSession } from "../auth/session";
import { goTo as navigate } from "../nav";
import { MapView } from "../map/MapView";
import { ClockPanel } from "./ClockPanel";
import { Player, type SeekRequest } from "./Player";
import { SyncExport } from "./SyncExport";
import {
  Lanes,
  showFilter,
  showFromUrl,
  timelineUrl,
  useLanesAndEvents,
  type Range,
} from "./TimelinePage";

const HOUR = 3_600_000;
const DAY = 24 * HOUR;
const utcDay = (ms: number) => Math.floor(ms / DAY) * DAY;

function atFromUrl(): number | null {
  const at = Date.parse(new URLSearchParams(window.location.search).get("at") ?? "");
  return Number.isNaN(at) ? null : at;
}

/**
 * Playing a session found on the Timeline page (design §7.2): the sources
 * around the chosen time in sync, with the day's lanes to move about in,
 * clock corrections, synchronised export and the map.
 */
export function PlaybackPage() {
  const [show] = useState(showFromUrl);
  const [start] = useState(atFromUrl);
  const [seek, setSeek] = useState<SeekRequest | null>(() =>
    start === null
      ? null
      : {
          utcMs: start,
          n: 1,
          play: new URLSearchParams(window.location.search).get("play") === "1",
        },
  );
  const [playhead, setPlayhead] = useState<number | null>(start);
  const [range, setRange] = useState<Range | null>(() => {
    if (start === null) return null;
    const q = new URLSearchParams(window.location.search);
    const [from, to] = [Date.parse(q.get("from") ?? ""), Date.parse(q.get("to") ?? "")];
    return from < to && from <= start && start <= to
      ? { from, to }
      : { from: utcDay(start), to: utcDay(start) + DAY };
  });
  const [brush, setBrush] = useState<Range | null>(null);
  const [exporting, setExporting] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [jump, setJump] = useState("");
  const session = useSession();
  const filter = showFilter(show);
  const { timeline, events } = useLanesAndEvents(range, show);

  const seekTo = (utcMs: number, play?: boolean) => {
    setSeek((s) => ({ utcMs, n: (s?.n ?? 0) + 1, play }));
    setPlayhead(utcMs);
  };
  const back = () => navigate(timelineUrl(show, playhead ?? start ?? undefined));
  const goTo = () => {
    const t = Date.parse(`${jump.replace(" ", "T")}Z`);
    if (Number.isNaN(t)) return;
    setRange({ from: utcDay(t), to: utcDay(t) + DAY });
    seekTo(t);
  };

  if (start === null || !range || !seek)
    return (
      <Stack spacing={2} component="section">
        <Typography variant="h5" component="h2">
          Playback
        </Typography>
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={back}>
              Timeline
            </Button>
          }
        >
          Find recordings on the Timeline page, then choose a time to play.
        </Alert>
      </Stack>
    );

  return (
    <Stack spacing={3} component="section">
      <Stack direction="row" sx={{ gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        <Typography variant="h5" component="h2" sx={{ flexGrow: 1 }}>
          Playback
        </Typography>
        <TextField
          size="small"
          label="Go to (UTC)"
          placeholder="2026-10-09 14:45"
          value={jump}
          onChange={(e) => setJump(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && goTo()}
        />
        <Button variant="outlined" onClick={goTo}>
          Go
        </Button>
        <Button onClick={back}>Back to the timeline</Button>
      </Stack>

      <Player
        seek={seek}
        filter={{ library: filter.library, case: filter.case }}
        onTime={setPlayhead}
        onPlaying={setPlaying}
        onClose={back}
        map={
          <MapView
            lanes={timeline.data?.lanes ?? []}
            playhead={playhead}
            onSeek={seekTo}
            span={range.to - range.from < DAY ? range : null}
            area={null}
            onArea={() => {}}
            onAreaIntervals={() => {}}
            height={300}
          />
        }
      />

      <Lanes
        range={range}
        onBrush={setBrush}
        lanes={timeline.data?.lanes ?? []}
        events={events.data?.events ?? []}
        truncated={timeline.data?.truncated ?? false}
        loading={timeline.isPending}
        onRange={setRange}
        playhead={playhead}
        onSeek={seekTo}
        playing={playing}
        onPause={() =>
          setSeek((s) => ({
            utcMs: playhead ?? s?.utcMs ?? 0,
            n: (s?.n ?? 0) + 1,
            pause: true,
          }))
        }
      />

      {filter.case && (
        <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
          <Button variant="outlined" onClick={() => setExporting(true)}>
            Export these times
          </Button>
          <Typography variant="body2" color="text.secondary">
            The times in view: drag across the lanes to zoom to the times you
            want.
          </Typography>
        </Stack>
      )}
      {exporting && filter.case && (
        <SyncExport
          caseId={filter.case}
          range={range}
          lanes={timeline.data?.lanes ?? []}
          onClose={() => setExporting(false)}
        />
      )}

      <ClockPanel
        range={range}
        filter={{ library: filter.library, case: filter.case }}
        lanes={timeline.data?.lanes ?? []}
        canCorrect={!!session.data && hasPermission(session.data, "Import")}
        canMarkReference={
          !!session.data && hasPermission(session.data, "ManageSources")
        }
      />

      <MapView
        lanes={timeline.data?.lanes ?? []}
        playhead={playhead}
        onSeek={seekTo}
        span={brush ?? (range.to - range.from < DAY ? range : null)}
        area={null}
        onArea={() => {}}
        onAreaIntervals={() => {}}
      />
    </Stack>
  );
}
